import { useState } from 'react';
import { Button, Card, Col, Form, Row, Table } from 'react-bootstrap';

import { ItemAvailability, LevelRewardInput, SongpopLegacyChestAmountMode } from '../../../../../../__gqltypes__';
import EditRewardsModal from '../../../components/EditRewardsModal';
import { formatReward } from '../../../components/formatReward';
import { ChestPoolDraft, ChestPoolItemRow, formatChance, newChestItemRow } from '../chestPool';

type Props = {
  // Stable id prefix for the form controls.
  poolId: string;
  pool: ChestPoolDraft;
  onChange: (pool: ChestPoolDraft) => void;
};

const parseInteger = (raw: string): number => Math.max(0, Math.floor(Number(raw) || 0));

// The item only (amount 1): the amount lives in the Exact / Range columns.
const itemLabel = (item: ChestPoolItemRow): string =>
  formatReward({ type: item.rewardType, amount: 1, itemId: item.itemId });

/**
 * Loot pool of the daily chest: item rows (any Wheel of Fortune reward, picked with the same
 * reward editor), raw weight with its derived chance, an Exact-or-Range amount per item, and the
 * number of items each opening gives.
 */
export function ChestPoolTable({ poolId, pool, onChange }: Props): JSX.Element {
  const [editItemIndex, setEditItemIndex] = useState<number | null>(null);

  const totalWeight = pool.items.reduce((sum, item) => sum + (Number.isFinite(item.weight) ? item.weight : 0), 0);

  const updateItem = (index: number, patch: Partial<ChestPoolItemRow>) => {
    onChange({ ...pool, items: pool.items.map((item, i) => (i === index ? { ...item, ...patch } : item)) });
  };

  const handleEditItemSave = (newReward: LevelRewardInput) => {
    if (editItemIndex !== null) {
      // The modal's amount is ignored: amounts live in the Exact / Range columns.
      updateItem(editItemIndex, { rewardType: newReward.type, itemId: newReward.itemId ?? null });
    }
    setEditItemIndex(null);
  };

  const editedItem = editItemIndex !== null ? pool.items[editItemIndex] ?? null : null;

  const rewardsPerChestValid =
    Number.isInteger(pool.itemsPerOpening) && pool.itemsPerOpening >= 1 && pool.itemsPerOpening <= pool.items.length;

  return (
    <Card className="mb-3">
      <Card.Header>
        <strong>Chest loot</strong>{' '}
        <span className="text-muted small">
          rolled at every claim: items drawn by weight without repeats, each amount exact or rolled in its range
        </span>
      </Card.Header>
      <Card.Body>
        {/* The one number that decides what a chest gives: framed so it is not missed. */}
        <div
          className="rounded px-3 py-2 mb-3"
          style={{
            border: `2px solid ${rewardsPerChestValid ? '#0d6efd' : '#dc3545'}`,
            backgroundColor: rewardsPerChestValid ? '#f0f6ff' : '#fff5f5',
          }}
        >
          <div className="d-flex align-items-center">
            <Form.Label className="font-weight-bold mb-0 mr-3" htmlFor={`${poolId}-items-per-opening`}>
              <i className="fas fa-gift mr-2" />
              Rewards per chest
            </Form.Label>
            <Form.Control
              id={`${poolId}-items-per-opening`}
              type="number"
              min={1}
              max={pool.items.length}
              className="font-weight-bold text-center"
              style={{
                width: 90,
                fontSize: '1.1rem',
                borderColor: rewardsPerChestValid ? undefined : '#dc3545',
              }}
              value={pool.itemsPerOpening}
              onChange={(e) => onChange({ ...pool, itemsPerOpening: parseInteger(e.target.value) })}
            />
            <span className="ml-2 text-muted">of {pool.items.length}</span>
          </div>
          <small className={`d-block mt-1 ${rewardsPerChestValid ? 'text-muted' : 'text-danger'}`}>
            {rewardsPerChestValid
              ? `Each opening gives ${pool.itemsPerOpening} of the ${pool.items.length} rewards below, picked by weight, never the same one twice.`
              : `Pick 1 to ${pool.items.length}: one chest cannot give more rewards than the list below has.`}
          </small>
        </div>
        <Table bordered responsive hover size="sm">
          <thead className="text-center">
            <tr>
              <th style={{ width: 40 }}>#</th>
              <th>Item</th>
              <th style={{ width: 110 }}>Weight</th>
              <th style={{ width: 90 }}>Chance</th>
              <th style={{ width: 130 }}>Amount mode</th>
              <th style={{ width: 190 }}>Amount(s)</th>
              <th style={{ width: 50 }} />
            </tr>
          </thead>
          <tbody>
            {pool.items.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center text-muted">
                  The pool needs at least one item.
                </td>
              </tr>
            )}
            {pool.items.map((item, index) => (
              <tr key={item._clientId}>
                <td className="align-middle text-center">{index + 1}</td>
                <td className="align-middle">
                  <Button variant="link" size="sm" className="p-0 text-left" onClick={() => setEditItemIndex(index)}>
                    {itemLabel(item)}
                  </Button>
                </td>
                <td className="align-middle">
                  <Form.Control
                    type="number"
                    min={0}
                    step="any"
                    value={item.weight}
                    onChange={(e) => updateItem(index, { weight: Number(e.target.value) || 0 })}
                  />
                </td>
                <td className="align-middle text-center">{formatChance(item.weight, totalWeight)}</td>
                <td className="align-middle">
                  <Form.Check
                    inline
                    type="radio"
                    id={`${poolId}-${item._clientId}-exact`}
                    name={`${poolId}-${item._clientId}-mode`}
                    label="Exact"
                    checked={item.amountMode === SongpopLegacyChestAmountMode.EXACT}
                    onChange={() => updateItem(index, { amountMode: SongpopLegacyChestAmountMode.EXACT })}
                  />
                  <Form.Check
                    inline
                    type="radio"
                    id={`${poolId}-${item._clientId}-range`}
                    name={`${poolId}-${item._clientId}-mode`}
                    label="Range"
                    checked={item.amountMode === SongpopLegacyChestAmountMode.RANGE}
                    onChange={() => updateItem(index, { amountMode: SongpopLegacyChestAmountMode.RANGE })}
                  />
                </td>
                <td className="align-middle">
                  {item.amountMode === SongpopLegacyChestAmountMode.EXACT ? (
                    <Form.Control
                      type="number"
                      min={1}
                      aria-label="Exact amount"
                      value={item.amount}
                      onChange={(e) => updateItem(index, { amount: parseInteger(e.target.value) })}
                    />
                  ) : (
                    <div className="d-flex align-items-center">
                      <Form.Control
                        type="number"
                        min={1}
                        aria-label="Min amount"
                        value={item.minAmount}
                        onChange={(e) => updateItem(index, { minAmount: parseInteger(e.target.value) })}
                      />
                      <span className="mx-1 text-muted">–</span>
                      <Form.Control
                        type="number"
                        min={1}
                        aria-label="Max amount"
                        value={item.maxAmount}
                        onChange={(e) => updateItem(index, { maxAmount: parseInteger(e.target.value) })}
                      />
                    </div>
                  )}
                </td>
                <td className="align-middle text-center">
                  <Button
                    size="sm"
                    variant="outline-danger"
                    title="Remove item"
                    onClick={() => onChange({ ...pool, items: pool.items.filter((_, i) => i !== index) })}
                  >
                    <i className="fas fa-trash" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <div className="d-flex justify-content-between align-items-center">
          <Button
            size="sm"
            variant="outline-primary"
            onClick={() => onChange({ ...pool, items: [...pool.items, newChestItemRow()] })}
          >
            <i className="fas fa-plus mr-1" />
            Add item
          </Button>
          <span className="text-muted small">
            Total weight: {totalWeight} — chances are rounded to one decimal and may not total exactly 100.0%.
          </span>
        </div>
      </Card.Body>
      {/* Same reward editor settings as the Wheel of Fortune rewards: every reward type. */}
      <EditRewardsModal
        isEdit={true}
        tier={null}
        level={0}
        show={editItemIndex !== null}
        reward={editedItem ? { type: editedItem.rewardType, amount: 1, itemId: editedItem.itemId } : null}
        handleSave={handleEditItemSave}
        handleClose={() => setEditItemIndex(null)}
        disableCurrencySpecialEvent
        disableMonthlyBuff
        enableItemLevelEdit
        liveOpsAvailabilities={[ItemAvailability.EARN]}
      />
    </Card>
  );
}
